<template>
  <div class="p-4">
    <!-- Action Header -->
    <div class="flex justify-end mb-4 border-b pb-4 no-print">
      <v-btn
        color="primary"
        small
        depressed
        @click="generatePDF"
        :loading="loading"
        class="font-weight-black rounded-sm h-9 px-6 uppercase tracking-widest"
      >
        <v-icon left size="18">mdi-file-pdf-box</v-icon>
        Download SOA
      </v-btn>
    </div>

    <!-- SOA Preview Area -->
    <div id="soa-content" class="soa-container">
      <!-- Header Row -->
      <div class="flex justify-between items-start mb-6">
        <div class="text-[0.7rem] italic leading-tight pt-2">
          Statement of Account as on {{ $dayjs().format('DD-MM-YYYY') }}
        </div>

        <!-- Text-based Logo following Foreclosure style -->
        <div class="text-right">
          <div
            class="font-bold text-[#1e3a8a]"
            style="
              font-family: serif;
              letter-spacing: -1px;
              font-size: 26px;
              line-height: 1;
              text-transform: uppercase;
            "
          >
            {{ orgNameParts[0] }}
          </div>
          <div
            class="text-right tracking-[0.3em] text-[#1e40af]"
            style="
              margin-top: 2px;
              font-size: 10px;
              text-transform: uppercase;
              font-family: sans-serif;
            "
          >
            {{ orgNameParts[1] }}
          </div>
        </div>
      </div>

      <div class="divider mb-4"></div>

      <!-- Company Section -->
      <div class="company-section mb-6">
        <h2 class="text-md font-bold uppercase">Zavron Finance Private Limited</h2>
        <p class="text-[10px] leading-tight">
          Registered Office:- 125,Zavron finance pvt ltd,near mure memorial hospital,maharajbagh
          road,sitabuldi,Nagpur,Maharastra-440012
        </p>
        <p class="text-[10px] leading-tight">
          Branch office:- 125,near mure memorial hospital,maharajbagh road , NAGPUR , Maharashtra -
          440012
        </p>
        <p class="text-[10px] leading-tight font-semibold mt-1">CIN:- U67100MH20117PTC292183</p>
      </div>

      <div class="divider mb-4"></div>

      <!-- Borrower & Guarantor Details -->
      <div class="details-section mb-6">
        <div class="mb-4">
          <h3 class="details-title">Borrower Details:-</h3>
          <div class="details-content">
            <div class="flex">
              <span class="label">Borrower Name:-</span>
              <span class="value font-bold">{{ selectedCase.customer?.customerFullName }}</span>
            </div>
            <div class="flex items-start">
              <span class="label">Addrss:-</span>
              <span class="value">{{ borrowerAddress }}</span>
            </div>
            <div class="flex">
              <span class="label">Mob No:-</span>
              <span class="value">{{
                selectedCase.customer?.customerPhoneNumber ||
                (selectedCase.customer?.customerMobile && selectedCase.customer?.customerMobile[0])
              }}</span>
            </div>
          </div>
        </div>

        <div class="divider-thin mb-4"></div>

        <div class="mb-4" v-if="guarantor">
          <h3 class="details-title">Guarantor Details:-</h3>
          <div class="details-content">
            <div class="flex">
              <span class="label">Guarantor Name:-</span>
              <span class="value font-bold">{{ guarantor.customerFullName }}</span>
            </div>
            <div class="flex items-start">
              <span class="label">Addrss:-</span>
              <span class="value">{{ guarantorAddress }}</span>
            </div>
            <div class="flex">
              <span class="label">Mob No:-</span>
              <span class="value">{{
                guarantor.customerPhoneNumber ||
                (guarantor.customerMobile && guarantor.customerMobile[0])
              }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="divider mb-4"></div>

      <!-- Loan Summary Table -->
      <div class="mb-8">
        <h3 class="summary-title mb-2">Summary of Loan Details for {{ selectedCase.caseNo }} :-</h3>
        <table class="summary-table w-full border-collapse border border-black">
          <tbody>
            <tr>
              <td class="w-1/4 px-2 py-2 border border-black font-semibold">Loan Amount:-</td>
              <td class="w-1/4 px-2 py-2 border border-black">
                {{ formatCurrency(selectedCase.loanDetails?.loanAmountSanctioned || 0) }}
              </td>
              <td class="w-1/4 px-2 py-2 border border-black font-semibold">
                Loan Sanction Date:-
              </td>
              <td class="w-1/4 px-2 py-2 border border-black">
                {{ formatDate(selectedCase.loanDetails?.sanctionDate) }}
              </td>
            </tr>
            <tr>
              <td class="px-2 py-2 border border-black font-semibold">Inerest Amount:-</td>
              <td class="px-2 py-2 border border-black">{{ formatCurrency(totalInterest) }}</td>
              <td class="px-2 py-2 border border-black font-semibold">Emi Start Date:-</td>
              <td class="px-2 py-2 border border-black">
                {{ formatDate(selectedCase.loanDetails?.emiStartDate) }}
              </td>
            </tr>
            <tr>
              <td class="px-2 py-2 border border-black font-semibold">Tenure:-</td>
              <td class="px-2 py-2 border border-black">{{ emiSchedule.length }} Months</td>
              <td class="px-2 py-2 border border-black font-semibold">Emi End Date:-</td>
              <td class="px-2 py-2 border border-black">{{ lastEmiDate }}</td>
            </tr>
            <tr>
              <td class="px-2 py-2 border border-black font-semibold">Interest Rate:-</td>
              <td class="px-2 py-2 border border-black">
                {{ selectedCase.loanDetails?.interestRate }}%
              </td>
              <td class="px-2 py-2 border border-black font-semibold">Frequency:-</td>
              <td class="px-2 py-2 border border-black">
                {{ selectedCase.loanDetails?.emiFrequency || 'Monthly' }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Transaction Table Section -->
      <div class="text-center mb-4">
        <h2 class="text-[#00A1E4] text-xl font-normal underline decoration-1 underline-offset-4">
          II - Statement of Accounts
        </h2>
        <p class="text-sm font-semibold mt-1">
          For the period ending {{ $dayjs().format('DD-MM-YYYY') }}
        </p>
        <p
          class="text-[10px] font-bold uppercase mt-1 tracking-widest border-b border-black inline-block pb-0.5"
        >
          Billed Transactions
        </p>
      </div>

      <div class="">
        <table class="transaction-table w-full border-collapse text-[11px]">
          <thead>
            <tr class="border-y border-black">
              <th class="border-x border-black px-2 py-2 text-left bg-gray-50">Date</th>
              <th class="border-x border-black px-2 py-2 text-left bg-gray-50">Transaction</th>
              <th class="border-x border-black px-2 py-2 text-left bg-gray-50">Voucher No</th>
              <th class="border-x border-black px-2 py-2 text-left bg-gray-50">Instrument No</th>
              <th class="border-x border-black px-2 py-2 text-left bg-gray-50">Remarks</th>
              <th class="border-x border-black px-2 py-2 text-right bg-gray-50">Due</th>
              <th class="border-x border-black px-2 py-2 text-right bg-gray-50">Receipts</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, i) in flattenedTransactions" :key="i" class="border-b border-gray-300">
              <td class="border-x border-black px-2 py-1.5">{{ formatDate(row.date) }}</td>
              <td class="border-x border-black px-2 py-1.5">{{ row.transaction }}</td>
              <td class="border-x border-black px-2 py-1.5">{{ row.voucherNo }}</td>
              <td class="border-x border-black px-2 py-1.5">{{ row.instrumentNo }}</td>
              <td class="border-x border-black px-2 py-1.5">{{ row.remarks }}</td>
              <td class="border-x border-black px-2 py-1.5 text-right">
                {{ row.due > 0 ? formatCurrency(row.due) : '' }}
              </td>
            </tr>

            <!-- LPC Due Row -->
            <tr v-if="lpiTotal.due > 0" class="border-b border-black font-semibold">
              <td class="border-x border-black px-2 py-1.5">
                {{ $dayjs().format('DD-MM-YYYY') }}
              </td>
              <td class="border-x border-black px-2 py-1.5" colspan="3">LPC Due</td>
              <td class="border-x border-black px-2 py-1.5"></td>
              <td class="border-x border-black px-2 py-1.5 text-right">
                {{ formatCurrency(lpiTotal.due) }}
              </td>
              <td class="border-x border-black px-2 py-1.5 text-right"></td>
            </tr>
          </tbody>
          <tfoot>
            <tr class="font-bold text-[#00A1E4] border-y border-black">
              <td colspan="5" class="border-x border-black px-2 py-2 text-center uppercase">Total</td>
              <td class="border-x border-black px-2 py-2 text-right">
                {{ formatCurrency(transactionTotals.due) }}
              </td>
              <td class="border-x border-black px-2 py-2 text-right">
                {{ formatCurrency(transactionTotals.receipts) }}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <!-- LPI Break Up Section -->
      <div class="mb-8" v-if="lpiBreakup.length > 0">
        <h2 class="text-[#00A1E4] text-center text-xl font-normal mb-2">LPI Break Up</h2>
        <table class="transaction-table w-full border-collapse text-[11px]">
          <thead>
            <tr class="border-y border-black">
              <th class="border-x border-black px-2 py-2 text-left bg-gray-50">EMI #</th>
              <th class="border-x border-black px-2 py-2 text-left bg-gray-50">Date</th>
              <th class="border-x border-black px-2 py-2 text-left bg-gray-50">Total LPI</th>
              <th class="border-x border-black px-2 py-2 text-right bg-gray-50">Reciept</th>
              <th class="border-x border-black px-2 py-2 text-right bg-gray-50">Due</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, i) in lpiBreakup" :key="i" class="border-b border-gray-300">
              <td class="border-x border-black px-2 py-1.5">{{ row.emiNo }}</td>
              <td class="border-x border-black px-2 py-1.5">{{ formatDate(row.date) }}</td>
              <td class="border-x border-black px-2 py-1.5">{{ row.totalLpi }}</td>
              <td class="border-x border-black px-2 py-1.5 text-right">{{ row.receipt }}</td>
              <td class="border-x border-black px-2 py-1.5 text-right font-semibold">
                {{ row.due }}
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr class="font-bold text-[#00A1E4]">
              <td colspan="2" class="border-x border-black px-2 py-2 text-center uppercase">Total</td>
              <td class="border-x border-black px-2 py-2">{{ lpiTotal.totalLpi }}</td>
              <td class="border-x border-black px-2 py-2 text-right">{{ lpiTotal.receipt }}</td>
              <td class="border-x border-black px-2 py-2 text-right">{{ lpiTotal.due }}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <!-- Total Due Break Up Section -->
      <div class="mb-8">
        <h2 class="text-[#00A1E4] text-center text-xl font-normal mb-2">Total Due Break Up</h2>
        <table class="summary-table w-full border-collapse border border-black">
          <tbody>
            <tr>
              <td class="w-2/3 px-4 py-2 border border-black font-semibold text-center">EMI Dues</td>
              <td class="w-1/3 px-4 py-2 border border-black text-center">
                {{ formatCurrency(totalDueBreakdown.emiDues) }}
              </td>
            </tr>
            <tr>
              <td class="px-4 py-2 border border-black font-semibold text-center">LPI Dues</td>
              <td class="px-4 py-2 border border-black text-center">
                {{ formatCurrency(totalDueBreakdown.lpiDues) }}
              </td>
            </tr>
            <tr>
              <td class="px-4 py-2 border border-black font-semibold text-center">VAS / HL Dues</td>
              <td class="px-4 py-2 border border-black text-center">
                {{ formatCurrency(totalDueBreakdown.vasDues) }}
              </td>
            </tr>
            <tr>
              <td class="px-4 py-2 border border-black font-semibold text-center">
                Collection Charges
              </td>
              <td class="px-4 py-2 border border-black text-center">
                {{ formatCurrency(totalDueBreakdown.collectionCharges) }}
              </td>
            </tr>
            <tr class="font-bold text-[#00A1E4]">
              <td class="px-4 py-2 border border-black text-center uppercase">Total</td>
              <td class="px-4 py-2 border border-black text-center">
                {{ formatCurrency(totalDueBreakdown.total) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import html2pdf from 'html2pdf.js'
import { getFullAddress } from '@/utils/common'

export default {
  name: 'CustomerSOA',
  props: {
    selectedCase: {
      type: Object,
      required: true
    },
    orgName: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      loading: false
    }
  },
  computed: {
    emiSchedule() {
      return this.selectedCase?.loanDetails?.emiSchedule || []
    },
    borrowerAddress() {
      const customer = this.selectedCase?.customer
      if (!customer) return 'N/A'
      const addr =
        customer.customerAddress && customer.customerAddress.length > 0
          ? customer.customerAddress[0]
          : customer
      return getFullAddress(addr)
    },
    guarantor() {
      return (this.selectedCase?.guarantors && this.selectedCase.guarantors.length > 0
        ? this.selectedCase.guarantors[0]
        : this.selectedCase?.loanDetails?.guarantors?.[0]) || null
    },
    guarantorAddress() {
      if (!this.guarantor) return 'N/A'
      const addr =
        this.guarantor.customerAddress && this.guarantor.customerAddress.length > 0
          ? this.guarantor.customerAddress[0]
          : this.guarantor
      return getFullAddress(addr)
    },
    orgNameParts() {
      const name = this.orgName || 'ZAVRON FINSERV'
      const parts = name.split(' ')
      if (parts.length > 1) {
        return [parts[0], parts.slice(1).join(' ')]
      }
      return [name, '']
    },
    lpiBreakup() {
      const breakup = []
      const today = this.$dayjs().startOf('day')
      const lpcAmountPerMonth = 600

      this.emiSchedule.forEach((emi, index) => {
        // Only ignore 'Paid' status (on-time payments)
        if (emi.emiStatus === 'Paid') {
          return
        }

        const dueDate = this.$dayjs(emi.dueDate).startOf('day')

        // Calculate months overdue
        let monthsOverdue = 0

        if (emi.emiStatus === 'Late' || emi.emiStatus === 'Late Paid') {
          // For late-paid EMIs
          const payDate = emi.paymentDate ? this.$dayjs(emi.paymentDate).startOf('day') : today
          if (payDate.isAfter(dueDate)) {
            monthsOverdue = payDate.diff(dueDate, 'month')
            if (monthsOverdue === 0) monthsOverdue = 1
          }
        } else {
          // For unpaid EMIs
          if (today.isAfter(dueDate)) {
            monthsOverdue = today.diff(dueDate, 'month')
            if (monthsOverdue === 0) monthsOverdue = 1
          }
        }

        if (monthsOverdue > 0) {
          const totalLpi = monthsOverdue * lpcAmountPerMonth
          // Check if any late fee was already paid in emiPayments
          const receipt = (emi.emiPayments || [])
            .filter((p) => !p.isDeleted)
            .reduce((sum, p) => sum + (parseFloat(p.latePaymentAmount) || 0), 0)

          breakup.push({
            emiNo: index + 1,
            date: emi.dueDate,
            totalLpi: totalLpi,
            receipt: receipt,
            due: Math.max(0, totalLpi - receipt)
          })
        }
      })

      return breakup
    },
    lpiTotal() {
      return this.lpiBreakup.reduce(
        (acc, row) => {
          acc.totalLpi += row.totalLpi
          acc.receipt += row.receipt
          acc.due += row.due
          return acc
        },
        { totalLpi: 0, receipt: 0, due: 0 }
      )
    },
    totalDueBreakdown() {
      const emiDues = this.emiSchedule
        .filter((emi) => !['Paid', 'Late', 'Late Paid'].includes(emi.emiStatus))
        .reduce((sum, emi) => sum + (parseFloat(emi.emiAmount) || 0), 0)

      const lpiDues = this.lpiTotal.due

      const vasDues = (this.selectedCase?.loanDetails?.additionalCharges || [])
        .filter((c) => !c.isDeleted && !c.isDeduction)
        .reduce((sum, charge) => {
          const net =
            (parseFloat(charge.amount) || 0) +
            (parseFloat(charge.metaData?.gstAmount) || 0) -
            (parseFloat(charge.metaData?.discountAmount) || 0) -
            (parseFloat(charge.metaData?.waiverAmount) || 0)
          return sum + net
        }, 0)

      const collectionCharges = this.emiSchedule.reduce((sum, emi) => {
        const unpaidCollection = (emi.emiPayments || [])
          .filter((p) => !p.isDeleted)
          .reduce((pSum, p) => pSum + (parseFloat(p.collectionCharges) || 0), 0)
        // Usually collection charges are added as dues then paid.
        // If we want "dues", we might need to know if they were actually billed.
        // For now, setting to 0 or sum of unpaid if logic exists.
        return sum + 0
      }, 0)

      return {
        emiDues,
        lpiDues,
        vasDues,
        collectionCharges,
        total: emiDues + lpiDues + vasDues + collectionCharges
      }
    },
    transactionTotals() {
      const transDue = this.flattenedTransactions.reduce((sum, row) => sum + (row.due || 0), 0)
      const transReceipts = this.flattenedTransactions.reduce(
        (sum, row) => sum + (row.receipt || 0),
        0
      )
      return {
        due: transDue + this.lpiTotal.due,
        receipts: transReceipts
      }
    },
    totalRepayable() {
      return this.emiSchedule.reduce((sum, emi) => sum + (emi.emiAmount || 0), 0)
    },
    totalInterest() {
      const principal = parseFloat(this.selectedCase.loanDetails?.loanAmountSanctioned || 0)
      const interest = this.emiSchedule.reduce((sum, emi) => sum + (emi.interestAmount || 0), 0)
      return interest > 0 ? interest : this.totalRepayable - principal
    },
    lastEmiDate() {
      if (!this.emiSchedule.length) return 'N/A'
      return this.formatDate(this.emiSchedule[this.emiSchedule.length - 1].dueDate)
    },
    flattenedTransactions() {
      const rows = []
      const schedule = this.emiSchedule || []

      schedule.forEach((emi, index) => {
        // Installment Row
        // Only show installment row if it's not a dummy/zero entry
        if (parseFloat(emi.emiAmount) > 1) {
          const isPaidEmi = String(emi.emiStatus || '').toLowerCase() === 'paid'
          rows.push({
            date: emi.dueDate,
            transaction: isPaidEmi ? `Installment (EMI #${index + 1})` : `Due (EMI #${index + 1})`,
            voucherNo: '',
            instrumentNo: '',
            remarks: '',
            due: parseFloat(emi.emiAmount) || 0,
            receipt: 0,
            isInstallment: true
          })
        }

        // Detailed Payments
        if (emi.emiPayments && emi.emiPayments.length) {
          const activePayments = emi.emiPayments.filter((p) => !p.isDeleted)
          activePayments.forEach((payment) => {
            // Add Late Payment Amount as a charge row if exists
            if (parseFloat(payment.latePaymentAmount) > 0) {
              rows.push({
                date: payment.date,
                transaction: `Late Fee Charges (EMI #${index + 1})`,
                voucherNo: payment.voucherNo || payment.receiptNo || payment.transactionId || '',
                instrumentNo: payment.utrNo || payment.chequeNo || '',
                remarks: 'Late Payment Penalty',
                due: parseFloat(payment.latePaymentAmount),
                receipt: 0,
                isCharge: true
              })
            }

            // Add Collection Charges as a charge row if exists
            if (parseFloat(payment.collectionCharges) > 0) {
              rows.push({
                date: payment.date,
                transaction: `Collection Charges (EMI #${index + 1})`,
                voucherNo: payment.voucherNo || payment.receiptNo || payment.transactionId || '',
                instrumentNo: payment.utrNo || payment.chequeNo || '',
                remarks: 'Field Collection Charges',
                due: parseFloat(payment.collectionCharges),
                receipt: 0,
                isCharge: true
              })
            }

            rows.push({
              date: payment.date,
              transaction: `EMI Receipt (EMI #${index + 1})`,
              voucherNo: payment.voucherNo || payment.receiptNo || payment.transactionId || '',
              instrumentNo: payment.utrNo || payment.chequeNo || '',
              remarks: payment.paymentMode || 'Payment',
              due: 0,
              receipt: parseFloat(payment.amount) || 0,
              isPayment: true
            })
          })
        } else if (emi.emiStatus?.toLowerCase() === 'paid' || emi.paidAmount > 0) {
          // Legacy payment support
            rows.push({
              date: emi.paymentDate || emi.paidDate || emi.dueDate,
              transaction: `EMI Receipt (EMI #${index + 1})`,
              voucherNo: emi.voucherNo || emi.receiptNo || emi.transactionId || '',
              instrumentNo: emi.chequeNo || emi.utrNo || '',
              remarks: emi.paymentMode || 'Legacy Payment',
              due: 0,
              receipt: parseFloat(emi.paidAmount) || parseFloat(emi.emiAmount) || 0,
              isPayment: true
            })
        }
      })

      // Additional Charges (VAS)
      const additionalCharges = this.selectedCase?.loanDetails?.additionalCharges || []
      additionalCharges.forEach((charge) => {
        const netAmount =
          (parseFloat(charge.amount) || 0) +
          (parseFloat(charge.metaData?.gstAmount) || 0) -
          (parseFloat(charge.metaData?.discountAmount) || 0) -
          (parseFloat(charge.metaData?.waiverAmount) || 0)

        rows.push({
          date: charge.dueDate || this.selectedCase.loanDetails?.sanctionDate,
          transaction: charge.name || charge.feeType || 'Additional Charge',
          voucherNo: '',
          instrumentNo: '',
          remarks: charge.remarks || 'VAS/Additional Charge',
          due: netAmount,
          receipt: 0,
          isCharge: true
        })
      })

      // Sort by date then by type (Installment/Charge before Payment if on same date)
      return rows.sort((a, b) => {
        const dateA = new Date(a.date)
        const dateB = new Date(b.date)
        if (dateA.getTime() !== dateB.getTime()) {
          return dateA - dateB
        }
        // Priority: Installment (1), Charge (2), Receipt (3)
        const typeA = a.isInstallment ? 1 : a.isCharge ? 2 : 3
        const typeB = b.isInstallment ? 1 : b.isCharge ? 2 : 3
        return typeA - typeB
      })
    }
  },
  methods: {
    formatCurrency(value) {
      if (typeof value !== 'number') value = parseFloat(value) || 0
      if (value === 0) return '0.00'
      return new Intl.NumberFormat('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value)
    },
    formatDate(date) {
      if (!date) return 'N/A'
      return this.$dayjs(date).format('DD-MM-YYYY')
    },
    async generatePDF() {
      this.loading = true
      const element = document.getElementById('soa-content')
      const originalMaxW = element.style.maxWidth
      element.style.maxWidth = '100%'

      const opt = {
        margin: [10, 10, 10, 10],
        filename: `SOA_${this.selectedCase.caseNo}_${this.selectedCase.customer?.customerFullName}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 3, useCORS: true, letterRendering: true, scrollY: 0 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
      }

      try {
        await html2pdf().set(opt).from(element).save()
        element.style.maxWidth = originalMaxW
        return true
      } catch (error) {
        console.error('PDF Generation Error:', error)
        this.$toast.error('Failed to generate PDF')
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.soa-container {
  font-family: 'Times New Roman', Times, serif;
  max-width: 800px;
  margin: 0 auto;
  color: #000;
  background: white;
  padding: 10px;
  box-sizing: border-box;
}

.divider {
  border-top: 1.5px solid #000;
}

.divider-thin {
  border-top: 1px solid #000;
}

.details-title {
  font-weight: bold;
  text-decoration: underline;
  text-underline-offset: 3px;
  font-size: 0.85rem;
}

.details-content {
  margin-top: 6px;
  line-height: 1.6;
  font-size: 0.75rem;
}

.details-content .label {
  min-width: 130px;
  font-weight: 500;
}

.summary-title {
  font-weight: bold;
  font-size: 0.85rem;
}

.summary-table td {
  font-size: 0.75rem;
  padding-top: 6px !important;
  padding-bottom: 6px !important;
}

.transaction-table th {
  font-weight: bold;
  font-size: 0.7rem;
}

.transaction-table td {
  padding-top: 4px !important;
  padding-bottom: 4px !important;
}

@media print {
  .no-print {
    display: none;
  }
  .soa-container {
    max-width: 100% !important;
    padding: 0;
  }
}
</style>
